import { useCallback, useEffect, useRef } from "react";
import Axios from "axios";
import { useAtom } from "jotai";
import { environment } from "../../environment";
import { errorCatcher } from "../../hooks";
import { useLogin } from "../login";
import { DCLicensingModel } from "../design_creator/types";
import {
    AddToStorefrontResult,
    BrandCatalogUsage,
    BrandCatalogUsagePage,
    CatalogUsageStatus,
    CatalogView,
    ProductListingDesign,
    ProductListingPage,
    ProductListingSort,
    brandCatalogUsageAtom,
    productListingLoadingAtom,
    productListingDesignsAtom,
} from "./types";

export interface BrowseQuery {
    page: number;
    limit: number;
    search?: string;
    brandIds?: number[];
    models?: DCLicensingModel[];
    view?: CatalogView;
    sort?: ProductListingSort;
}

// #region normalizeDesign
/**
 * The catalog endpoint returns the raw `dc_designs` row, so the artwork lives on
 * `current_version`, the owner brand arrives as a flat `brand_name`, and the
 * individual creator as `created_by_user`. Flatten to the card's shape here, at
 * the single boundary, rather than teaching every component the wire format.
 */
const normalizeDesign = (raw: any): ProductListingDesign => ({
    ...raw,
    image_url: raw?.image_url ?? raw?.current_version?.image_url ?? null,
    preview_url: raw?.preview_url ?? raw?.current_version?.preview_url ?? null,
    brand: raw?.brand ?? (raw?.brand_name ? { id: raw?.brand_id, name: raw.brand_name } : null),
    creator: raw?.creator ?? raw?.created_by_user ?? null,
});

export interface UsageQuery {
    page: number;
    limit: number;
    status?: CatalogUsageStatus | 'ALL';
}

// #region useProductListing
/**
 * Catalog browse + licensee management. Deliberately separate from the
 * designer-catalog service: same visual pattern, different backend concept.
 */
export const useProductListing = () => {
    const [listing, setListing] = useAtom(productListingDesignsAtom);
    const [usage, setUsage] = useAtom(brandCatalogUsageAtom);
    const [loading, setLoading] = useAtom(productListingLoadingAtom);

    const [, authToken, loginFunctions] = useLogin();
    const loginFunctionsRef = useRef(loginFunctions);
    useEffect(() => { loginFunctionsRef.current = loginFunctions; }, [loginFunctions]);

    const headers = { 'X-Wallmates-Auth': authToken };

    // #region fetchBrowse
    const fetchBrowse = useCallback(async (query: BrowseQuery): Promise<ProductListingPage | null> => {
        setLoading(true);
        const params = new URLSearchParams();
        params.set('page', String(query.page));
        params.set('limit', String(query.limit));
        if (query.search) params.set('search', query.search);
        if (query.sort) params.set('sort', query.sort);
        (query.brandIds ?? []).forEach((id) => params.append('brand_id', String(id)));
        (query.models ?? []).forEach((model) => params.append('model', model));
        if (query.view && query.view !== 'all') params.set('view', query.view);

        const result = await Axios.get(`${environment.catalogUrl}?${params.toString()}`, { headers })
            .catch((err: any) => errorCatcher(err, loginFunctionsRef.current.logout));

        setLoading(false);
        if (!result) {
            setListing({ rows: [], total: 0, page: query.page, limit: query.limit });
            return null;
        }

        // Tolerate either a bare array or a paged envelope.
        const data: any = result.data;
        const page: ProductListingPage = Array.isArray(data)
            // GET /v2/catalog returns `{ designs, total, brandCounts }` — `rows` is
            // only a fallback for a differently-shaped envelope.
            ? { rows: data.map(normalizeDesign), total: data.length, page: query.page, limit: query.limit }
            : {
                rows: (data?.designs ?? data?.rows ?? []).map(normalizeDesign),
                total: data?.total ?? 0,
                page: data?.page ?? query.page,
                limit: data?.limit ?? query.limit,
            };

        setListing(page);
        return page;
    }, [authToken]); // eslint-disable-line

    // #region fetchDesign
    const fetchDesign = useCallback(async (designId: number | string) => {
        const result = await Axios.get(`${environment.catalogUrl}/designs/${designId}`, { headers })
            .catch((err: any) => errorCatcher(err, loginFunctionsRef.current.logout));
        return result?.data ? normalizeDesign(result.data) : null;
    }, [authToken]); // eslint-disable-line

    // #region setPreference
    /**
     * Favourite / hide a design for this brand. Send only the flag you are
     * changing — the server preserves the other one.
     *
     * Updates the loaded page in place so the card re-renders immediately; the
     * caller decides whether the row should also leave the current view (it must,
     * when hiding from "All" or un-favouriting inside "Favorites").
     */
    const setPreference = useCallback(async (
        designId: number,
        patch: { is_favorite?: boolean; is_hidden?: boolean }
    ): Promise<boolean> => {
        const result = await Axios.put(`${environment.catalogUrl}/designs/${designId}/preference`, patch, { headers })
            .catch((err: any) => errorCatcher(err, loginFunctionsRef.current.logout));

        if (!result?.data) return false;

        const saved = result.data as { is_favorite: boolean; is_hidden: boolean };
        setListing((current) => current && ({
            ...current,
            rows: current.rows.map((row) => row.id === Number(designId)
                ? { ...row, is_favorite: saved.is_favorite, is_hidden: saved.is_hidden }
                : row),
        }));
        return true;
    }, [authToken]); // eslint-disable-line

    // #region addToStorefront
    /**
     * Returns the raw status so the dialog can treat 409 (already added) as a
     * normal outcome and surface 400/402 server copy verbatim.
     */
    const addToStorefront = useCallback(async (
        designId: number | string,
        retailPricingTemplateId: number,
        includeGiftwrap = false
    ): Promise<AddToStorefrontResult> => {
        try {
            const response = await Axios.post(
                `${environment.catalogUrl}/designs/${designId}/add`,
                { retail_pricing_template_id: retailPricingTemplateId, include_giftwrap: includeGiftwrap },
                { headers }
            );
            return {
                ok: true,
                status: response.status,
                shopify_product_id: response.data?.shopify_product_id ?? response.data?.product_id ?? null,
                giftwrap_shopify_product_id: response.data?.giftwrap_shopify_product_id ?? null,
                giftwrap_warning: response.data?.giftwrap_warning,
                usage_id: response.data?.id ?? null,
                online_store_publish: response.data?.online_store_publish ?? null,
            };
        } catch (err: any) {
            const status = err?.response?.status ?? 0;
            const message = err?.response?.data?.message
                || err?.response?.data?.error
                || 'Could not add this design to your storefront.';
            return { ok: false, status, message };
        }
    }, [authToken]); // eslint-disable-line

    // #region fetchUsage
    const fetchUsage = useCallback(async (query: UsageQuery): Promise<BrandCatalogUsagePage | null> => {
        setLoading(true);
        const params = new URLSearchParams();
        params.set('page', String(query.page));
        params.set('limit', String(query.limit));
        if (query.status && query.status !== 'ALL') params.set('status', query.status);

        const result = await Axios.get(`${environment.catalogUrl}/usage?${params.toString()}`, { headers })
            .catch((err: any) => errorCatcher(err, loginFunctionsRef.current.logout));

        setLoading(false);
        if (!result) {
            setUsage({ rows: [], total: 0, page: query.page, limit: query.limit });
            return null;
        }

        const data: any = result.data;
        const page: BrandCatalogUsagePage = Array.isArray(data)
            // GET /v2/catalog/usage returns `{ usages, total }`.
            ? { rows: data, total: data.length, page: query.page, limit: query.limit }
            : { rows: data?.usages ?? data?.rows ?? [], total: data?.total ?? 0, page: data?.page ?? query.page, limit: data?.limit ?? query.limit };

        setUsage(page);
        return page;
    }, [authToken]); // eslint-disable-line

    // #region setAutoRenew
    const setAutoRenew = useCallback(async (usageId: number, autoRenew: boolean): Promise<BrandCatalogUsage | null> => {
        try {
            const response = await Axios.patch(
                `${environment.catalogUrl}/usage/${usageId}/auto-renew`,
                { auto_renew: autoRenew },
                { headers }
            );
            return (response.data ?? null) as BrandCatalogUsage | null;
        } catch {
            return null;
        }
    }, [authToken]); // eslint-disable-line

    // #region cancelUsage
    const cancelUsage = useCallback(async (usageId: number): Promise<{ ok: boolean; message?: string }> => {
        try {
            await Axios.delete(`${environment.catalogUrl}/usage/${usageId}`, { headers });
            return { ok: true };
        } catch (err: any) {
            return { ok: false, message: err?.response?.data?.message || 'Could not remove this design.' };
        }
    }, [authToken]); // eslint-disable-line

    // #region resyncUsageImages
    /**
     * Re-push the owner's full image set onto this licence's Shopify listing.
     * Responds 202 — the upload runs in a background poller, so the returned row
     * reads NEEDS_RESYNC rather than SYNCED and the table shows a pending badge
     * until the next refetch.
     */
    const resyncUsageImages = useCallback(async (usageId: number): Promise<{ ok: boolean; usage?: BrandCatalogUsage; message?: string }> => {
        try {
            const response = await Axios.post(
                `${environment.catalogUrl}/usage/${usageId}/resync-images`,
                {},
                { headers }
            );
            return { ok: true, usage: (response.data ?? undefined) as BrandCatalogUsage | undefined };
        } catch (err: any) {
            return { ok: false, message: err?.response?.data?.message || 'Could not start an image resync.' };
        }
    }, [authToken]); // eslint-disable-line

    return { listing, usage, loading, fetchBrowse, fetchDesign, setPreference, addToStorefront, fetchUsage, setAutoRenew, cancelUsage, resyncUsageImages };
};
